import { useMemo } from 'react'
import { type AccessControlConfig } from '../../../legacy/components/AccessControl/accessControl.utils'
import useAccessControl from '../../../legacy/components/AccessControl/useAccessControl'
import { StripeAssociationsAccessControl } from '../../../legacy/routes/settings/stripe/StripeAssociations.configuration'
import { ICPlatformVersion } from '../../../utils/ic-platform/versioning'

// Mirrors `externalHealthDashboardAccessControl` in
// `nav-v2/components/HelpMenu/hooks/useHelpMenuLinks.ts` so the System status
// entry is hidden for users who can't reach the partner-status page.
const SystemStatusAccessControl = {
  warehouseFeatureToggles: ['rt_ipp_external_health_dashboard'],
  permissions: ['external_health_dashboard.view'],
  minICPlatformVersion: ICPlatformVersion.r2026_01_0,
} as unknown as AccessControlConfig

// Mirrors `retailerContactViewAccessControl` in
// `domains/retailer-contact/src/access-control/retailerContactAccess.configuration.ts`.
// Inlined here to avoid a circular dependency: importing from the
// `@retailer-platform/domain-retailer-contact` barrel pulls in
// `useDomainScopedPathname`/`useDomainGoToPath` from the dashboard's own
// routing exports while the dashboard package is still loading.
const RetailerContactViewAccessControl = {
  permissions: ['retailer_contact_management.view'],
} as unknown as AccessControlConfig

// v3 settings sidebar IA — Figma node 10911:107655. A 273px sidebar that mounts
// alongside the primary v3 nav whenever the active route belongs to a settings
// page. Two sections (User management / Platform management); every item is
// wired to a route name resolved against CoreContext.routesByName.

export type NavSettingsV3Item = {
  // i18n key for the item label.
  name: string
  // Route name (from CoreContext.routesByName). When omitted, the item is a
  // placeholder for a destination that doesn't exist yet.
  route?: string
  // Optional gate evaluated by useNavHierarchySettingsV3 — items whose
  // accessControl fails are dropped from the returned sections (and from
  // indexedRoutes). Mirrors v2's NavMenuHierarchyL2.accessControl.
  accessControl?: AccessControlConfig
}

export type NavSettingsV3Section = {
  // i18n key for the section header label.
  name: string
  items: NavSettingsV3Item[]
}

export type NavHierarchySettingsV3 = {
  sections: NavSettingsV3Section[]
  // Route name → true. The sidebar uses this lookup to decide whether to render
  // (when the active route is a key in this map) and which item is highlighted.
  indexedRoutes: Record<string, true>
}

const indexSettingsRoutes = (sections: NavSettingsV3Section[]): Record<string, true> => {
  const result: Record<string, true> = {}
  sections.forEach(section => {
    section.items.forEach(item => {
      if (item.route) result[item.route] = true
    })
  })
  return result
}

export const useNavHierarchySettingsV3 = (): NavHierarchySettingsV3 => {
  const hasAccess = useAccessControl()
  return useMemo(() => {
    const rawSections: NavSettingsV3Section[] = [
      {
        name: 'navV3.settings.userManagement.title',
        items: [
          {
            name: 'navV3.settings.users.title',
            route: 'partner-account-management-list',
          },
          { name: 'navV3.settings.activityLog.title', route: 'audit-logs-list' },
          { name: 'navV3.settings.notifications.title', route: 'notifications-settings' },
          { name: 'navV3.settings.security.title', route: 'retailer-security' },
          {
            name: 'navV3.settings.technicalSupportContacts.title',
            route: 'retailer-contact',
            accessControl: RetailerContactViewAccessControl,
          },
        ],
      },
      {
        name: 'navV3.settings.platformManagement.title',
        items: [
          {
            name: 'navV3.settings.stripeAssociations.title',
            route: 'stripeSettings',
            accessControl: StripeAssociationsAccessControl,
          },
          { name: 'navV3.settings.integrations.title', route: 'integrations-list' },
          { name: 'navV3.settings.retailerProfile.title', route: 'onboarding-retailer-settings' },
          { name: 'navV3.settings.storeConfigAdmin.title', route: 'store-configuration-drafts' },
          { name: 'navV3.settings.iosOnboarding.title', route: 'storefront-onboarding-ios' },
          {
            name: 'navV3.settings.androidOnboarding.title',
            route: 'storefront-onboarding-android',
          },
          { name: 'navV3.settings.webOnboarding.title', route: 'storefront-onboarding-web' },
          { name: 'navV3.settings.storefrontSetup.title', route: 'onboarding-site-setup' },
          {
            name: 'navV3.settings.systemStatus.title',
            route: 'external-health-dashboard-partner-status',
            accessControl: SystemStatusAccessControl,
          },
        ],
      },
    ]
    const sections = rawSections
      .map(section => ({
        ...section,
        items: section.items.filter(item => !item.accessControl || hasAccess(item.accessControl)),
      }))
      .filter(section => section.items.length > 0)
    return { sections, indexedRoutes: indexSettingsRoutes(sections) }
  }, [hasAccess])
}
